import { Box, useMediaQuery, useTheme } from "@mui/material";
import { MainNavigation } from "../navigation/MainNavigation";
import { Fragment, PropsWithChildren, useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { AccountStore } from "@root/stores/accountStore";
import { LoggedInNavigation } from "../navigation/LoggedInNavigation";
import { usePageContext } from "@root/renderer/usePageContext";
import { isAccountUrlPath } from "@root/utils/functions";
import { Sidebar } from "./Sidebar";
import { sidebarStore } from "@root/stores/sidebarStore";
import { NotificationProvider } from "@root/contexts/NotificationContext";

export const MainLayout = observer(
  ({ store, children }: PropsWithChildren<{ store: AccountStore }>) => {
    const { urlPathname } = usePageContext();
    const theme = useTheme();
    const mediumScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { authModalStore } = store;

    // Ensure sidebar state is restored from localStorage after page load
    useEffect(() => {
      sidebarStore.refreshFromLocalStorage();
    }, []);

    const showLogin = useCallback(
      () => authModalStore.openLoginModal(),
      [authModalStore],
    );

    const showSignUp = useCallback(
      () => authModalStore.openAuthModal("register"),
      [authModalStore],
    );

    // Show hamburger on all pages (including index page for sidebar search functionality)
    const showHamburger = true;

    // Calculate sidebar width for content adjustment
    const sidebarWidth = sidebarStore.isOpen && !mediumScreen ? 280 : 0;

    return (
      <Fragment>
        {store?.account ? (
          <NotificationProvider
            pollingInterval={
              Number(import.meta.env.PUBLIC_ENV__NOTIFICATIONTIME) || 30000
            }
          >
            <LoggedInNavigation showHamburger={showHamburger} />
            <Sidebar isOpen={sidebarStore.isOpen} />
            <Box
              sx={(theme) => ({
                marginLeft: `${sidebarWidth}px`,
                padding:
                  isAccountUrlPath(urlPathname) && mediumScreen
                    ? "0"
                    : isAccountUrlPath(urlPathname)
                      ? "1.5rem"
                      : "0",
                height: "calc(100% - 68px)", // Account pages have AppBar
                transition: "margin-left 0.3s ease-in-out",
                [theme.breakpoints.down("md")]: {
                  marginLeft: "0", // No margin on mobile (sidebar is overlay)
                  height: "calc(100% - 64px)", // Mobile AppBar height
                },
                [theme.breakpoints.down("sm")]: {
                  height: "calc(100% - 60px)", // Small mobile AppBar height
                },
              })}
            >
              {children}
            </Box>
          </NotificationProvider>
        ) : (
          <>
            <MainNavigation
              onLoginClick={showLogin}
              onSignUpClick={showSignUp}
            />
            <Box
              sx={{
                padding: "0",
                height: "100%",
              }}
            >
              {children}
            </Box>
          </>
        )}
      </Fragment>
    );
  },
);
